import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { useNavigate } from 'react-router-dom';

import { useNotification } from '../Notification/NotificationContext';
import * as actions from '../../store/modules/auth/actions';
import { resetSessionGuard } from '../../services/axios';

// Single-flight do tratamento de 401: com várias requisições em voo, todas voltam
// 401 e cada uma tentaria um re-login completo (um bcrypt por tentativa no
// servidor). O controle é de módulo, e não de componente, porque existem várias
// instâncias de ErrorProcessor montadas ao mesmo tempo.
const RECOVERY_COOLDOWN = 10000;
let lastRecoveryAt = 0;

export default function ErrorProcessor({ error }) {
  const navigate = useNavigate();
  const notification = useNotification();
  const dispatch = useDispatch();
  const rememberUser = useSelector((state) => state.auth.remember);
  const loginError = useSelector((state) => state.auth.loginError);

  useEffect(() => {
    if (error) {
      // Requisição cancelada localmente pelo guard de sessão: não é um erro de
      // verdade e já foi tratada pelo 401 que a originou.
      if (error.isSessionExpired) return;

      const status = get(error, 'response.status', 0);
      let erros = get(error, 'response.data.errors', []);

      if (typeof erros !== 'object') {
        erros = [erros];
      }

      if (status === 401) {
        const now = Date.now();
        if (now - lastRecoveryAt < RECOVERY_COOLDOWN) return;
        lastRecoveryAt = now;

        if (rememberUser && loginError === 0) {
          dispatch(
            actions.loginRequest({
              email: rememberUser.email,
              password: rememberUser.password,
              remember: true,
            })
          );

          navigate(window.location.pathname, { replace: true });
        } else {
          resetSessionGuard();
          dispatch(actions.loginFailure());
          navigate('/login');
        }
        return;
      }

      if (status === 403) {
        navigate('/403');
        notification(erros.join('. '), 'danger');
        return;
      }

      if (status > 299) {
        // Tenta extrair mensagem de erro específica do backend
        let errorMessage = 'Erro ao processar página';

        if (erros.length > 0) {
          errorMessage = erros.join('. ');
        } else {
          // Tenta extrair mensagem de erro do campo 'error' ou 'message'
          const backendError =
            get(error, 'response.data.error', '') ||
            get(error, 'response.data.message', '');
          if (backendError) {
            errorMessage = backendError;
          }
        }

        notification(errorMessage, 'danger', 5000);
      }
    }
  }, [dispatch, error, loginError, navigate, notification, rememberUser]);

  return <span />;
}

ErrorProcessor.defaultProps = {
  error: {},
};

ErrorProcessor.propTypes = {
  error: PropTypes.shape({
    isSessionExpired: PropTypes.bool,
  }),
};
